// ===========================================
// DetailClient — Refonte UX (header riche + KPIs + onglets enrichis)
// ===========================================
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
    Card, Tabs, Table, Tag, Button, Tooltip, Typography, Dropdown,
} from 'antd';
import {
    FileProtectOutlined, DollarOutlined, WarningOutlined, EyeOutlined,
    FileSearchOutlined, EditOutlined, PhoneOutlined, MailOutlined,
    EnvironmentOutlined, BankOutlined, CalendarOutlined,
    PrinterOutlined, PlusOutlined, MoreOutlined, ReloadOutlined,
    ArrowLeftOutlined,
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';

import { clientsService } from '../../common/services';
import { formatPhone, formatMontant, formatDate } from '../../common/utils/format';
import DetailSkeleton from '../../components/detailSkeleton';
import PrintHeader from '../../components/printHeader';
import PrintFooter from '../../components/printFooter';

import ClientAvatar from './components/ClientAvatar';
import './detail-client.page.scss';

const { Text } = Typography;

// ─── Format relatif (cohérent avec liste clients/dashboard) ──────────────
const formatRelative = (date) => {
    if (!date) return '—';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '—';
    const now = new Date();
    const diffMs = now - d;
    if (diffMs < 0) return formatDate(date);

    const diffMin = Math.floor(diffMs / 60000);
    const diffHr = Math.floor(diffMs / 3600000);
    const diffDay = Math.floor(diffMs / 86400000);
    const diffMonth = Math.floor(diffDay / 30);
    const diffYear = Math.floor(diffDay / 365);

    if (diffMin < 1)    return "À l'instant";
    if (diffMin < 60)   return `Il y a ${diffMin} min`;
    if (diffHr  < 24)   return `Il y a ${diffHr} h`;
    if (diffDay < 30)   return diffDay === 1 ? 'Hier' : `Il y a ${diffDay} jours`;
    if (diffMonth < 12) return `Il y a ${diffMonth} mois`;
    return `Il y a ${diffYear} an${diffYear > 1 ? 's' : ''}`;
};

// ─── Statut contrat (couleurs unifiées) ──────────────────────────────────
const STATUT_COLORS = {
    actif: 'green',
    expire: 'red',
    resilie: 'volcano',
    renouvele: 'blue',
    suspendu: 'orange',
};

const MOYEN_PAIEMENT_COLORS = {
    especes: 'green',
    cheque: 'blue',
    virement: 'purple',
    mobile_money: 'cyan',
};

const SINISTRE_STATUT_COLORS = {
    ouvert: 'processing',
    en_cours: 'orange',
    clos: 'green',
    rejete: 'red',
};

const DetailClient = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [client, setClient] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('contrats');

    useEffect(() => {
        const fetchClient = async () => {
            try {
                const res = await clientsService.getById(id);
                setClient(res?.data || res);
            } catch (error) {
                toast.error('Erreur lors du chargement du client');
            } finally {
                setLoading(false);
            }
        };
        fetchClient();
    }, [id]);

    if (loading) return <DetailSkeleton kpis={4} sections={2} />;

    if (!client) {
        return (
            <div className="detail-client-empty">
                <Text type="secondary">Client introuvable</Text>
            </div>
        );
    }

    const clientName = client.type === 'entreprise'
        ? (client.raisonSociale || client.nom || '')
        : `${client.nom || ''} ${client.prenom || ''}`.trim();

    // Calculs financiers : ne compter que contrats en vie
    const contratsAvecSolde = (client.contrats || []).filter(
        (c) => c.statut !== 'resilie' && c.statut !== 'renouvele'
    );
    const totalPrimes = contratsAvecSolde.reduce((sum, c) => sum + (c.primeTTC || 0), 0);
    const contratIdsAvecSolde = new Set(contratsAvecSolde.map((c) => c._id));
    const totalVerse = (client.reglements || []).reduce((sum, r) => {
        const contratId = r.contrat?._id || r.contrat;
        if (contratId && !contratIdsAvecSolde.has(contratId)) return sum;
        return sum + (r.montant || 0);
    }, 0);
    const solde = Math.max(0, totalPrimes - totalVerse);
    const tauxRecouvrement = totalPrimes > 0
        ? Math.round((totalVerse / totalPrimes) * 100)
        : 0;
    const nbContrats = (client.contrats || []).length;
    const nbContratsActifs = (client.contrats || []).filter((c) => c.statut === 'actif').length;
    const nbReglements = (client.reglements || []).length;
    const nbSinistres = (client.sinistres || []).length;

    // ─── Colonnes Contrats (avec % payé) ──────────────────────────────
    const contratColumns = [
        {
            title: 'N° Police',
            dataIndex: 'numeroPolice',
            key: 'numeroPolice',
            render: (val) => <span className="detail-mono">{val || '—'}</span>,
            width: 130,
        },
        {
            title: 'Type',
            dataIndex: 'typeAssurance',
            key: 'typeAssurance',
            render: (t) => <Tag color="blue">{t?.nom || t || '—'}</Tag>,
            width: 140,
        },
        {
            title: 'Compagnie',
            dataIndex: ['compagnie', 'nom'],
            key: 'compagnie',
            ellipsis: true,
            responsive: ['md'],
        },
        {
            title: 'Statut',
            dataIndex: 'statut',
            key: 'statut',
            width: 100,
            render: (s) => <Tag color={STATUT_COLORS[s] || 'default'}>{s}</Tag>,
        },
        {
            title: 'Prime TTC',
            dataIndex: 'primeTTC',
            key: 'primeTTC',
            align: 'right',
            width: 120,
            render: (v) => <span className="detail-amount">{formatMontant(v || 0)}</span>,
        },
        {
            title: 'Effet',
            dataIndex: 'dateEffet',
            key: 'dateEffet',
            width: 110,
            render: (d) => formatDate(d),
            responsive: ['lg'],
        },
        {
            title: 'Échéance',
            dataIndex: 'dateEcheance',
            key: 'dateEcheance',
            width: 110,
            render: (d) => formatDate(d),
            responsive: ['lg'],
        },
        {
            title: 'Action',
            key: 'action',
            width: 140,
            align: 'center',
            render: (_, r) => (
                <div className="detail-action-cell">
                    <Button
                        type="primary"
                        size="small"
                        icon={<EyeOutlined />}
                        className="btn-detail"
                        onClick={() => navigate(`/dashboard/contrat/${r._id}`)}
                    >
                        Détail
                    </Button>
                </div>
            ),
        },
    ];

    // ─── Colonnes Règlements ──────────────────────────────────────────
    const reglementColumns = [
        {
            title: 'Date',
            dataIndex: 'dateReglement',
            key: 'date',
            render: (d) => formatDate(d),
            width: 110,
        },
        {
            title: 'N° Police',
            dataIndex: ['contrat', 'numeroPolice'],
            key: 'contrat',
            render: (t) => <span className="detail-mono">{t || '—'}</span>,
            width: 130,
        },
        {
            title: 'Montant',
            dataIndex: 'montant',
            key: 'montant',
            align: 'right',
            width: 130,
            render: (v) => <span className="detail-amount detail-amount-positive">{formatMontant(v || 0)}</span>,
        },
        {
            title: 'Mode',
            dataIndex: 'moyenPaiement',
            key: 'mode',
            width: 130,
            render: (m) => <Tag color={MOYEN_PAIEMENT_COLORS[m] || 'default'}>{m || '—'}</Tag>,
        },
        {
            title: 'Référence',
            dataIndex: 'reference',
            key: 'reference',
            render: (t) => t || '—',
            responsive: ['md'],
        },
    ];

    // ─── Colonnes Sinistres ───────────────────────────────────────────
    const sinistreColumns = [
        {
            title: 'N° Sinistre',
            dataIndex: 'numero',
            key: 'numero',
            render: (t) => <span className="detail-mono">{t || '—'}</span>,
            width: 130,
        },
        {
            title: 'Date',
            dataIndex: 'dateSinistre',
            key: 'date',
            render: (d) => formatDate(d),
            width: 110,
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            render: (t) => <Tag color="orange">{t || '—'}</Tag>,
            width: 130,
        },
        {
            title: 'Statut',
            dataIndex: 'statut',
            key: 'statut',
            width: 110,
            render: (s) => <Tag color={SINISTRE_STATUT_COLORS[s] || 'default'}>{s || '—'}</Tag>,
        },
        {
            title: 'Montant estimé',
            dataIndex: 'montantEstime',
            key: 'montant',
            align: 'right',
            width: 140,
            render: (v) => v ? <span className="detail-amount">{formatMontant(v)}</span> : '—',
            responsive: ['md'],
        },
    ];

    // ─── Onglets ──────────────────────────────────────────────────────
    const tabItems = [
        {
            key: 'contrats',
            label: (
                <span>
                    <FileProtectOutlined /> Contrats
                    <span className="detail-tab-count">{nbContrats}</span>
                </span>
            ),
            children: (
                <Table
                    dataSource={client.contrats || []}
                    columns={contratColumns}
                    rowKey="_id"
                    size="middle"
                    scroll={{ x: 'max-content' }}
                    locale={{ emptyText: 'Aucun contrat' }}
                    pagination={{ pageSize: 10, showTotal: (t) => `${t} contrat${t > 1 ? 's' : ''}` }}
                />
            ),
        },
        {
            key: 'reglements',
            label: (
                <span>
                    <DollarOutlined /> Règlements
                    <span className="detail-tab-count">{nbReglements}</span>
                </span>
            ),
            children: (
                <Table
                    dataSource={client.reglements || []}
                    columns={reglementColumns}
                    rowKey="_id"
                    size="middle"
                    scroll={{ x: 'max-content' }}
                    locale={{ emptyText: 'Aucun règlement' }}
                    pagination={{ pageSize: 10, showTotal: (t) => `${t} règlement${t > 1 ? 's' : ''}` }}
                />
            ),
        },
        {
            key: 'sinistres',
            label: (
                <span>
                    <WarningOutlined /> Sinistres
                    <span className="detail-tab-count">{nbSinistres}</span>
                </span>
            ),
            children: (
                <Table
                    dataSource={client.sinistres || []}
                    columns={sinistreColumns}
                    rowKey="_id"
                    size="middle"
                    scroll={{ x: 'max-content' }}
                    locale={{ emptyText: 'Aucun sinistre' }}
                    pagination={{ pageSize: 10, showTotal: (t) => `${t} sinistre${t > 1 ? 's' : ''}` }}
                />
            ),
        },
    ];

    return (
        <div className="detail-client-page">
            <PrintHeader
                title={`Fiche client : ${clientName}`}
                subtitle={`Code : ${client.code || 'N/A'} — ${client.type === 'entreprise' ? 'Entreprise' : 'Particulier'}`}
                agenceName={client.agence?.nom}
            />

            {/* Layout CRM : sidebar profil à gauche | content à droite */}
            <div className="detail-client-layout">
                {/* ═════ COLONNE GAUCHE : PROFIL ═════ */}
                <aside className="detail-client-sidebar no-print">
                    <Card className="detail-client-sidebar-card">
                        {/* Retour à la liste */}
                        <button
                            type="button"
                            className="sidebar-back-btn"
                            onClick={() => navigate('/dashboard/clients')}
                            aria-label="Retour à la liste des clients"
                        >
                            <ArrowLeftOutlined />
                            <span>Retour à la liste des clients</span>
                        </button>

                        {/* Identité */}
                        <div className="sidebar-identity">
                            <ClientAvatar client={client} size={88} />
                            <h1 className="sidebar-name">{clientName || '—'}</h1>
                            <div className="sidebar-meta">
                                <Tag color={client.type === 'entreprise' ? 'blue' : 'green'}>
                                    {client.type === 'entreprise' ? 'Entreprise' : 'Particulier'}
                                </Tag>
                                <Tooltip title="Code client">
                                    <span className="detail-mono sidebar-code">
                                        {client.code || 'N/A'}
                                    </span>
                                </Tooltip>
                            </div>
                            <div className="sidebar-since">
                                <CalendarOutlined /> Client depuis {formatRelative(client.createdAt)}
                            </div>
                        </div>

                        <div className="sidebar-divider" />

                        {/* Coordonnées en stack vertical */}
                        <div className="sidebar-coord">
                            <div className="sidebar-coord-item">
                                <div className="sidebar-coord-icon"><PhoneOutlined /></div>
                                <div className="sidebar-coord-body">
                                    <div className="sidebar-coord-label">Téléphone</div>
                                    <div className="sidebar-coord-value">
                                        {client.telephone ? (
                                            <a href={`tel:${client.telephone}`} className="sidebar-coord-link">
                                                {formatPhone(client.telephone)}
                                            </a>
                                        ) : '—'}
                                    </div>
                                </div>
                            </div>

                            <div className="sidebar-coord-item">
                                <div className="sidebar-coord-icon"><MailOutlined /></div>
                                <div className="sidebar-coord-body">
                                    <div className="sidebar-coord-label">Email</div>
                                    <div className="sidebar-coord-value">
                                        {client.email ? (
                                            <a href={`mailto:${client.email}`} className="sidebar-coord-link">
                                                {client.email}
                                            </a>
                                        ) : '—'}
                                    </div>
                                </div>
                            </div>

                            <div className="sidebar-coord-item">
                                <div className="sidebar-coord-icon"><EnvironmentOutlined /></div>
                                <div className="sidebar-coord-body">
                                    <div className="sidebar-coord-label">Adresse</div>
                                    <div className="sidebar-coord-value">
                                        {client.adresse || client.ville || '—'}
                                    </div>
                                </div>
                            </div>

                            <div className="sidebar-coord-item">
                                <div className="sidebar-coord-icon"><BankOutlined /></div>
                                <div className="sidebar-coord-body">
                                    <div className="sidebar-coord-label">Agence</div>
                                    <div className="sidebar-coord-value">{client.agence?.nom || '—'}</div>
                                </div>
                            </div>
                        </div>

                    </Card>
                </aside>

                {/* ═════ COLONNE DROITE : KPIs + ONGLETS ═════ */}
                <main className="detail-client-main">
                    {/* KPIs (4 cartes) */}
                    <section className="detail-client-kpis no-print" aria-label="Indicateurs financiers">
                        <div className="detail-kpi detail-kpi-blue">
                            <div className="detail-kpi-icon"><FileProtectOutlined /></div>
                            <div className="detail-kpi-body">
                                <div className="detail-kpi-label">Contrats</div>
                                <div className="detail-kpi-value">{nbContrats}</div>
                                <div className="detail-kpi-sub">{nbContratsActifs} actif{nbContratsActifs > 1 ? 's' : ''}</div>
                            </div>
                        </div>

                        <div className="detail-kpi detail-kpi-purple">
                            <div className="detail-kpi-icon"><DollarOutlined /></div>
                            <div className="detail-kpi-body">
                                <div className="detail-kpi-label">Total primes</div>
                                <div className="detail-kpi-value">{formatMontant(totalPrimes)}</div>
                                <div className="detail-kpi-sub">engagement total</div>
                            </div>
                        </div>

                        <div className="detail-kpi detail-kpi-green">
                            <div className="detail-kpi-icon"><DollarOutlined /></div>
                            <div className="detail-kpi-body">
                                <div className="detail-kpi-label">Total versé</div>
                                <div className="detail-kpi-value">{formatMontant(totalVerse)}</div>
                                <div className="detail-kpi-sub">{tauxRecouvrement}% du total</div>
                            </div>
                        </div>

                        <div className={`detail-kpi ${solde > 0 ? 'detail-kpi-red' : 'detail-kpi-green'}`}>
                            <div className="detail-kpi-icon">
                                {solde > 0 ? <WarningOutlined /> : <FileProtectOutlined />}
                            </div>
                            <div className="detail-kpi-body">
                                <div className="detail-kpi-label">Solde restant</div>
                                <div className="detail-kpi-value">{formatMontant(solde)}</div>
                                <div className="detail-kpi-sub">
                                    {solde > 0 ? 'à recouvrer' : 'soldé ✓'}
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Onglets : Contrats / Règlements / Sinistres */}
                    <Card className="detail-client-tabs-card">
                        <Tabs
                            activeKey={activeTab}
                            onChange={setActiveTab}
                            items={tabItems}
                            size="middle"
                            tabBarExtraContent={{
                                right: (
                                    <div className="detail-client-tab-actions no-print">
                                        <Button
                                            type="primary"
                                            icon={<PlusOutlined />}
                                            onClick={() => navigate(`/dashboard/nouveau-contrat?clientId=${id}`)}
                                            className="sidebar-btn-new"
                                            size="small"
                                        >
                                            Nouveau contrat
                                        </Button>
                                        <Button
                                            icon={<EditOutlined />}
                                            onClick={() => navigate(`/dashboard/nouveau-client?id=${id}`)}
                                            className="sidebar-btn-edit"
                                            size="small"
                                        >
                                            Modifier
                                        </Button>
                                        <Button
                                            icon={<FileSearchOutlined />}
                                            onClick={() => navigate(`/dashboard/situation-compte/${id}`)}
                                            className="sidebar-btn-situation"
                                            size="small"
                                        >
                                            Situation
                                        </Button>
                                        <Button
                                            icon={<PrinterOutlined />}
                                            onClick={() => window.print()}
                                            className="sidebar-btn-print"
                                            size="small"
                                        >
                                            Imprimer
                                        </Button>
                                    </div>
                                ),
                            }}
                        />
                    </Card>
                </main>
            </div>

            <PrintFooter />
        </div>
    );
};

export default DetailClient;
